import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@common-fe/common-fe';
import { isNumber, toNumber } from 'lodash';

import { api } from '@/api';
import { API_URL } from '@/dtos';
import { getTaxYears } from '@/utils/handlers';

import { useRecipientsStore } from '../stores';
import { LimitsIRS } from '../types';

const existingTaxYears = getTaxYears();

interface PlanIdLimitAmountPayload {
  [planId: string]: number;
}
interface LimitPayload {
  employee_id: number;
  plan_id_to_contribution_amount: PlanIdLimitAmountPayload;
  plan_id_to_election_limit_amount: PlanIdLimitAmountPayload;
}

const formatData = (data?: LimitPayload[]) => {
  const limits: LimitsIRS = {};
  data?.forEach((employeeLimit) => {
    const planIdLimitAmount: PlanIdLimitAmountPayload = {};

    Object.keys(employeeLimit).forEach((key) => {
      if (key === 'plan_id_to_contribution_amount' || key === 'plan_id_to_election_limit_amount') {
        Object.keys(employeeLimit[key]).forEach((planId) => {
          const limit = employeeLimit[key][planId];
          const currentLimit = planIdLimitAmount[planId];
          const minLimit = Math.min(...[limit, currentLimit].filter((item) => typeof item === 'number'));
          if (typeof minLimit === 'number') {
            planIdLimitAmount[planId] = minLimit;
          }
        });
      }
    });

    if (isNumber(employeeLimit.employee_id) && Object.keys(planIdLimitAmount || {}).length) {
      limits[employeeLimit.employee_id] = planIdLimitAmount;
    }
  });
  return limits;
};

export interface EmployeeIdPlanIdForRequest {
  employeeId: string;
  planId: string;
}

interface IrsLimitRequest {
  employee_id: number;
  plan_id: number;
  tax_year: number;
}

export interface IrsLimitSearchRequest {
  requests: IrsLimitRequest[];
}

export const useGetLimitsQuery = () => {
  const [isLoading, setIsLoading] = useState(false);
  const limitsSetRef = useRef<Set<string>>(new Set());
  const { setLimits, applyLimitsToRecipients } = useRecipientsStore();
  const { mutateAsync } = useMutation((searchRequest: IrsLimitSearchRequest) =>
    // TODO: should be IEmployeeContributionLimitResponse but this change breaks a lot of code. For the future refactor
    api.post<LimitPayload[]>(API_URL.CONTRIBUTION_REQUESTS_IRS_LIMITS_SEARCH, searchRequest)
  );

  const handleSearch = useCallback(
    async (searchRequest: EmployeeIdPlanIdForRequest[]) => {
      if (!existingTaxYears?.length || !searchRequest?.length) return;

      await existingTaxYears.forEach(async (taxYear) => {
        const limitsKey = `${taxYear}_${JSON.stringify(searchRequest)}`;
        if (limitsSetRef.current.has(limitsKey)) return;
        limitsSetRef.current.add(limitsKey);

        setIsLoading(true);
        const response = await mutateAsync({
          requests: searchRequest.map((item) => ({
            employee_id: toNumber(item.employeeId),
            plan_id: toNumber(item.planId),
            tax_year: toNumber(taxYear),
          })),
        });
        if (!response?.data) return;
        const data = response.data;
        setLimits({ [taxYear]: formatData(data) });
        applyLimitsToRecipients();
      });
      setIsLoading(false);
    },
    [mutateAsync, applyLimitsToRecipients, setLimits, setIsLoading]
  );

  return {
    search: handleSearch,
    areLimitsLoading: isLoading,
  };
};
